/**
 * @license
 * Copyright Google Inc. All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */


import {Injectable, Optional, SkipSelf} from '@angular/core';


// Users of the Dispatcher never need to see this type, but TypeScript requires it to be exported.
export type UniqueSelectionDispatcherListener = (id: string, name: string) => void;
/**
 * Class to coordinate unique selection based on name.
 * Intended to be consumed as an Angular service.
 * This service is needed because native radio change events are only fired on the item currently
 * being selected, and we still need to uncheck the previous selection.
 * 
 * This service does not *store* any IDs and names because they may change at any time, so it is
 * less error-prone if they are simply passed through when the events occur.
 */
export class UniqueSelectionDispatcher {
private _listeners: UniqueSelectionDispatcherListener[] = [];
/**
 * Notify other items that selection for the given name has been set.
 * @param {?} id ID of the item.
 * @param {?} name Name of the item.
 * @return {?}
 */
notify(id: string, name: string) {
    for (let /** @type {?} */ listener of this._listeners) {
      listener(id, name);
    }
  }
/**
 * Listen for future changes to item selection.
 * @param {?} listener
 * @return {?}
 */
listen(listener: UniqueSelectionDispatcherListener) {
    this._listeners.push(listener);
  }
static decorators: DecoratorInvocation[] = [
{ type: Injectable },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
];
}

function UniqueSelectionDispatcher_tsickle_Closure_declarations() {
/** @type {?} */
UniqueSelectionDispatcher.decorators;
/**
 * @nocollapse
 * @type {?}
 */
UniqueSelectionDispatcher.ctorParameters;
/** @type {?} */
UniqueSelectionDispatcher.prototype._listeners;
}

/**
 * @param {?} parentDispatcher
 * @return {?}
 */
export function UNIQUE_SELECTION_DISPATCHER_PROVIDER_FACTORY(
    parentDispatcher: UniqueSelectionDispatcher) {
  return parentDispatcher || new UniqueSelectionDispatcher();
}

export const /** @type {?} */ UNIQUE_SELECTION_DISPATCHER_PROVIDER = {
  // If there is already a dispatcher available, use that. Otherwise, provide a new one.
  provide: UniqueSelectionDispatcher,
  deps: [[new Optional(), new SkipSelf(), UniqueSelectionDispatcher]],
  useFactory: UNIQUE_SELECTION_DISPATCHER_PROVIDER_FACTORY
};

interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
